import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';

const ContactCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [consentStatus, setConsentStatus] = useState<'granted' | 'denied' | 'unknown'>('unknown');
  const [source, setSource] = useState('manual');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Handle tag addition
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  // Handle tag deletion
  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };
  
  // Handle tag input keydown
  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput) {
      e.preventDefault();
      handleAddTag();
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      setError('First name, last name, and email are required');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
       // Create contact data with proper data types for Firestore
       const contactData = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone ? phone.trim() : '',
          company: company ? company.trim() : '',
          title: title ? title.trim() : '',
          tags: Array.isArray(tags) ? tags : [],
          notes: notes ? notes.trim() : '',
          engagementScore: 0,
          consentStatus,
          source,
          createdAt: serverTimestamp(),
          lastContactDate: null,
       };

       // Make sure we have a valid organizationId
       if (!id) {
          throw new Error('Missing organization ID');
       }

       // Save to Firestore
       const contactsRef = collection(db, `organizations/${id}/contacts`);
       const docRef = await addDoc(contactsRef, contactData);

       setSuccess(true);
       setTimeout(() => {
          // Force a refresh of the contacts list when navigating back
          // Adding a unique timestamp to the URL to force navigation and trigger data reload
          const timestamp = new Date().getTime();

          navigate(`/organizations/${id}/contacts?refresh=${timestamp}`);
       }, 1500);
    } catch (error) {
      console.error('Error creating contact:', error);
      setError('Failed to create contact. Please try again. Error: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with navigation */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/organizations/${id}/contacts`)}
          sx={{ mr: 2 }}
        >
          Back to Contacts
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Create New Contact
        </Typography>
      </Box>
      
      {/* Form */}
      <Paper sx={{ p: 3 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Contact created successfully! Redirecting...
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Professional Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  fullWidth
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Tags and Notes
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  label="Add Tags"
                  fullWidth
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button 
                          onClick={handleAddTag}
                          disabled={!tagInput.trim()}
                        >
                          Add
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Press Enter to add a tag"
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {tags.map(tag => (
                    <Chip 
                      key={tag} 
                      label={tag} 
                      onDelete={() => handleDeleteTag(tag)} 
                      color="primary" 
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Marketing Consent Status</FormLabel>
                  <RadioGroup 
                    row 
                    value={consentStatus} 
                    onChange={(e) => setConsentStatus(e.target.value as 'granted' | 'denied' | 'unknown')}
                  >
                    <FormControlLabel value="granted" control={<Radio />} label="Granted" />
                    <FormControlLabel value="denied" control={<Radio />} label="Denied" />
                    <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Contact Source</FormLabel>
                  <RadioGroup 
                    row 
                    value={source} 
                    onChange={(e) => setSource(e.target.value)}
                  >
                    <FormControlLabel value="manual" control={<Radio />} label="Manual Entry" />
                    <FormControlLabel value="import" control={<Radio />} label="Import" />
                    <FormControlLabel value="website" control={<Radio />} label="Website" />
                    <FormControlLabel value="event" control={<Radio />} label="Event" />
                    <FormControlLabel value="referral" control={<Radio />} label="Referral" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/organizations/${id}/contacts`)}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                Create Contact
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ContactCreate;
